import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography, SvgIcon, InputLabel, TextField, Autocomplete, Checkbox, Alert } from "@mui/material";
import { useEffect, useReducer, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useSelector } from "react-redux";
import { cloneDeep } from "lodash";
import { useBulkCreateContentModelFieldMutation, useCreateContentModelMutation, useGetContentModelFieldsQuery, useCreateContentItemMutation, useGetContentNavItemsQuery } from "../../../../../shell/services/instance";
import { ContentModel, User, ContentNavItem } from "../../../../../shell/services/types";
import { notify } from "../../../../../shell/store/notifications";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { modelIconMap, modelNameMap } from "../utils";
import { formatPathPart } from "../../../../../utility/formatPathPart";
import { AppState } from "../../../../../shell/store/types";
import { SelectBlockGroupInput } from "./SelectBlockGroupInput";
interface Props {
  onClose: () => void;
  model: ContentModel;
}
export const DuplicateModelDialogue = ({
  onClose,
  model
}: Props) => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [newModel, updateNewModel] = useReducer((prev: Partial<ContentModel>, next: Partial<ContentModel>) => {
    const newModel = {
      ...prev,
      ...next
    };
    if (prev.label !== newModel.label) {
      newModel.name = newModel.label.toLowerCase().replaceAll(" ", "_");
    } else {
      newModel.name = newModel.name.toLowerCase().replaceAll(" ", "_");
    }
    return newModel;
  }, {
    label: "",
    name: "",
    type: model.type,
    description: "",
    parentZUID: null,
    listed: true
  });
  const {
    data: fields
  } = useGetContentModelFieldsQuery({
    modelZUID: model?.ZUID
  });
  const [createModel, {
    isLoading: createModelIsLoading,
    isSuccess: createModelIsSuccess,
    error: createModelError,
    data: createModelData
  }] = useCreateContentModelMutation();
  const [createFields, {
    isLoading: createFieldsIsLoading,
    isSuccess: createFieldsIsSuccess,
    error: createFieldsError
  }] = useBulkCreateContentModelFieldMutation();
  const [createContentItem, {
    isLoading: isCreatingContentItem,
    isSuccess: isContentItemCreated,
    error: createContentItemError
  }] = useCreateContentItemMutation();
  const user: User = useSelector((state: AppState) => state.user);
  const {
    data: navItems
  } = useGetContentNavItemsQuery();
  const error = createModelError || createFieldsError || createContentItemError;
  useEffect(() => {
    if (createModelIsSuccess && createModelData) {
      if (newModel.type === "templateset") {
        // Create initial content item for templateset
        createInitialTemplatesetContent();
      } else {
        // For other model types, immediately just check if there are fields to duplicate
        if (fields?.length) {
          duplicateFields();
        } else {
          navigateToModelSchema();
        }
      }
    }
  }, [createModelIsSuccess]);
  useEffect(() => {
    if (isContentItemCreated) {
      // Flow only applies to templateset model types
      // Duplicate fields if there any
      if (fields?.length) {
        duplicateFields();
      } else {
        navigateToModelSchema();
      }
    }
  }, [isContentItemCreated]);
  useEffect(() => {
    if (createFieldsIsSuccess) {
      navigateToModelSchema();
    }
  }, [createFieldsIsSuccess]);
  useEffect(() => {
    if (error) {
      dispatch(notify({
        // @ts-ignore
        message: error?.data?.error || t("schema.duplicateModelFailed"),
        kind: "warn"
      }));
    }
  }, [error]);
  const parents = useMemo(() => {
    if (navItems) {
      const _navItems = cloneDeep(navItems);
      return _navItems?.sort((a, b) => a.label.localeCompare(b.label));
    }
    return [];
  }, [navItems]);
  const navigateToModelSchema = () => {
    history.push(`/schema/${createModelData?.data.ZUID}`);
    onClose();
  };
  const createInitialTemplatesetContent = () => {
    createContentItem({
      modelZUID: createModelData.data.ZUID,
      body: {
        web: {
          pathPart: formatPathPart(newModel.label),
          canonicalTagMode: 1,
          metaLinkText: newModel.label,
          metaTitle: newModel.label,
          parentZUID: newModel.parentZUID || "0"
        },
        meta: {
          contentModelZUID: createModelData.data.ZUID,
          createdByUserZUID: user.ZUID
        }
      }
    });
  };
  const duplicateFields = () => {
    const newFields = fields.filter(field => !field?.deletedAt).map(field => {
      const {
        ZUID,
        settings,
        ...rest
      } = field;
      return {
        ...rest,
        settings: {
          ...settings,
          list: settings?.list || false
        }
      };
    });
    createFields({
      modelZUID: createModelData.data.ZUID,
      fields: newFields
    });
  };
  return <Dialog open onClose={onClose} sx={{
    my: "20px"
  }} PaperProps={{
    sx: {
      maxWidth: "640px",
      maxHeight: "min(100%, 1000px)",
      m: 0
    }
  }} data-sentry-element="Dialog" data-sentry-component="DuplicateModelDialogue" data-sentry-source-file="DuplicateModelDialogue.tsx">
      <DialogTitle component="div" data-sentry-element="DialogTitle" data-sentry-source-file="DuplicateModelDialogue.tsx">
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" data-sentry-element="Stack" data-sentry-source-file="DuplicateModelDialogue.tsx">
          <Box display="flex" alignItems="center" gap={1} data-sentry-element="Box" data-sentry-source-file="DuplicateModelDialogue.tsx">
            <SvgIcon sx={{
            fontSize: "28px"
          }} color="action" component={modelIconMap[model.type]} data-sentry-element="SvgIcon" data-sentry-source-file="DuplicateModelDialogue.tsx" />
            <Stack data-sentry-element="Stack" data-sentry-source-file="DuplicateModelDialogue.tsx">
              <Typography variant="h5" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="DuplicateModelDialogue.tsx">
                {t("schema.duplicateModelTitle", {
                label: model.label
              })}
              </Typography>
              <Typography variant="body3" color="text.secondary" data-sentry-element="Typography" data-sentry-source-file="DuplicateModelDialogue.tsx">
                {t("schema.duplicateModelSubtitle", {
                type: t(modelNameMap[model.type])
              })}
              </Typography>
            </Stack>
          </Box>
          <IconButton size="small" onClick={() => onClose()} data-sentry-element="IconButton" data-sentry-source-file="DuplicateModelDialogue.tsx">
            <CloseRoundedIcon fontSize="small" data-sentry-element="CloseRoundedIcon" data-sentry-source-file="DuplicateModelDialogue.tsx" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{
      pt: 2.5,
      backgroundColor: "grey.50"
    }} data-sentry-element="DialogContent" data-sentry-source-file="DuplicateModelDialogue.tsx">
        <Box display="flex" flexDirection="column" gap={2.5} data-sentry-element="Box" data-sentry-source-file="DuplicateModelDialogue.tsx">
          <Alert severity="info" data-sentry-element="Alert" data-sentry-source-file="DuplicateModelDialogue.tsx">
            <Trans i18nKey="schema.duplicateModelAlertBodyRich" values={{
            label: model.label
          }} components={{
            strong: <strong />
          }} data-sentry-element="Trans" data-sentry-source-file="DuplicateModelDialogue.tsx" />
          </Alert>
          <Box data-sentry-element="Box" data-sentry-source-file="DuplicateModelDialogue.tsx">
            <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="DuplicateModelDialogue.tsx">
              {t("schema.duplicateModelDisplayNameLabel")}
            </InputLabel>
            <TextField inputProps={{
            maxLength: 100
          }} placeholder={t("schema.duplicateModelDisplayNamePlaceholder", {
            label: model.label
          })} value={newModel.label} onChange={event => updateNewModel({
            label: event.target.value
          })} fullWidth autoFocus data-sentry-element="TextField" data-sentry-source-file="DuplicateModelDialogue.tsx" />
          </Box>
          <Box data-sentry-element="Box" data-sentry-source-file="DuplicateModelDialogue.tsx">
            <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="DuplicateModelDialogue.tsx">
              {t("schema.duplicateModelReferenceIdLabel")}
            </InputLabel>
            <TextField inputProps={{
            maxLength: 100
          }} placeholder={t("schema.duplicateModelReferenceIdPlaceholder")} value={newModel.name} onChange={event => updateNewModel({
            name: event.target.value
          })} fullWidth data-sentry-element="TextField" data-sentry-source-file="DuplicateModelDialogue.tsx" />
          </Box>
          <Box data-sentry-element="Box" data-sentry-source-file="DuplicateModelDialogue.tsx">
            <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="DuplicateModelDialogue.tsx">{t("schema.duplicateModelParentLabel")}</InputLabel>
            <Autocomplete fullWidth renderInput={params => <TextField {...params} placeholder={t("schema.duplicateModelParentPlaceholder")} />} options={parents} onChange={(event, value: ContentNavItem) => updateNewModel({
            parentZUID: value?.ZUID || null
          })} sx={{
            "& .MuiOutlinedInput-root": {
              padding: "2px"
            }
          }} data-sentry-element="Autocomplete" data-sentry-source-file="DuplicateModelDialogue.tsx" />
          </Box>
          {/* Block grouping will be implemented at a different point  */}
          {/* {model?.type === "block" && (
            <SelectBlockGroupInput
              groupType="available"
              groupZUID=""
              newGroupName=""
              onGroupTypeChange={() => {}}
              onGroupZUIDChange={() => {}}
              onNewGroupNameChange={() => {}}
            />
           )} */}
          <Box data-sentry-element="Box" data-sentry-source-file="DuplicateModelDialogue.tsx">
            <InputLabel data-sentry-element="InputLabel" data-sentry-source-file="DuplicateModelDialogue.tsx">
              {t("schema.duplicateModelDescriptionLabel")}
            </InputLabel>
            <TextField inputProps={{
            maxLength: 500
          }} value={newModel.description} placeholder={t("schema.duplicateModelDescriptionPlaceholder")} onChange={event => updateNewModel({
            description: event.target.value
          })} fullWidth multiline rows={4} data-sentry-element="TextField" data-sentry-source-file="DuplicateModelDialogue.tsx" />
          </Box>
          <Box display="flex" gap={1} data-sentry-element="Box" data-sentry-source-file="DuplicateModelDialogue.tsx">
            <Checkbox sx={{
            width: "24px",
            height: "24px"
          }} checked={!!model?.listed} onChange={event => updateNewModel({
            listed: event.target.checked
          })} data-sentry-element="Checkbox" data-sentry-source-file="DuplicateModelDialogue.tsx" />
            <Box data-sentry-element="Box" data-sentry-source-file="DuplicateModelDialogue.tsx">
              <Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="DuplicateModelDialogue.tsx">
                {t("schema.duplicateModelListedLabel")}
              </Typography>
              <Typography component="p" variant="body3" color="text.secondary" fontWeight={600} data-sentry-element="Typography" data-sentry-source-file="DuplicateModelDialogue.tsx">
                {t("schema.duplicateModelListedDescription")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{
      pt: 2.5
    }} data-sentry-element="DialogActions" data-sentry-source-file="DuplicateModelDialogue.tsx">
        <Button variant="outlined" color="inherit" onClick={onClose} data-sentry-element="Button" data-sentry-source-file="DuplicateModelDialogue.tsx">
          {t("common.cancel")}
        </Button>
        <Button variant="contained" disabled={!newModel.name || !newModel.label} loading={!!createModelIsLoading || !!createFieldsIsLoading || !!isCreatingContentItem} onClick={() => createModel({
        ...newModel
      })} data-sentry-element="Button" data-sentry-source-file="DuplicateModelDialogue.tsx">
          {t("schema.duplicate")}
        </Button>
      </DialogActions>
    </Dialog>;
};